import type { AxiosError } from 'axios'
import axios from 'axios'
import isNil from 'lodash/isNil'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'

import {
  API_DOMAIN,
  API_RESPONSE_STATUS_FORBIDDEN,
  API_RESPONSE_STATUS_NOT_FOUND,
  API_RESPONSE_STATUS_UNAUTHORIZED,
  API_RESPONSE_STATUSES_SERVER_ERRORS,
} from '../config/api'
import { redirect404 } from '../navigation'
import { keycloak } from '../services/keycloak'

import type {
  ApiDeleteType,
  ApiGetType,
  ApiPatchType,
  ApiPostType,
  ApiPutType,
  ApiType,
} from './api.types'
import { ApiError } from './ApiError'

export const KeycloakProtectedApi = axios.create()

KeycloakProtectedApi.interceptors.request.use(async (config) => {
  const token = await keycloak.getToken()
  config.headers.Authorization = `Bearer ${token}`

  return config
})

function getHeaders(form?: boolean) {
  return {
    Accept: 'application/json',
    'Content-Type': form ? 'multipart/form-data' : 'application/json',
  }
}

export function getUrl(endpoint: string, domain?: string | undefined): string {
  const baseDomain = domain || API_DOMAIN

  return `${baseDomain}${endpoint}`
}

/**
 * Checks if the passed `error` is API error
 * @param error - Error
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * Check if API response status is 401
 */
export function isApiResponseStatusUnauthorized(status: number): boolean {
  return status === API_RESPONSE_STATUS_UNAUTHORIZED
}

/**
 * Checks if the passed `error` is API Unauthorized error
 * @param error - An error
 */
export function isApiUnauthorizedError(error: ApiError): boolean {
  return isApiError(error) && isApiResponseStatusUnauthorized(error.status)
}

/**
 * Check if API response status is 403
 */
export function isApiResponseStatusForbidden(status: number): boolean {
  return status === API_RESPONSE_STATUS_FORBIDDEN
}

/**
 * Checks if the passed `error` is API Forbidden error
 * @param error - An error
 */
export function isApiForbiddenError(error: ApiError): boolean {
  return isApiError(error) && isApiResponseStatusForbidden(error.status)
}

/**
 * Check if API response status is 404
 */
export function isApiResponseStatusNotFound(status: number): boolean {
  return status === API_RESPONSE_STATUS_NOT_FOUND
}

/**
 * Checks if the passed `error` is API Not found error
 * @param error - An error
 */
export function isApiNotFoundError(error: ApiError): boolean {
  return isApiError(error) && isApiResponseStatusNotFound(error.status)
}

/**
 * Check if API response status is server error
 * {@link API_RESPONSE_STATUSES_SERVER_ERRORS}
 */
export function isApiResponseStatusServerError(status: number): boolean {
  return API_RESPONSE_STATUSES_SERVER_ERRORS.includes(status)
}

/**
 * Checks if the passed `error` is API server error
 * @param error - An error
 */
export function isApiServerError(error: ApiError): boolean {
  return isApiError(error) && isApiResponseStatusServerError(error.status)
}

function handleError(
  error: AxiosError,
  ignore404Redirect?: Nullable<boolean>,
  ignore401Logout?: Nullable<boolean>
) {
  if (!error.response) {
    // Request was canceled
    return null
  }

  const { status, data } = error.response

  if (isApiResponseStatusServerError(status)) {
    throw new ApiError('Error.Server', status)
  }

  if (isApiResponseStatusNotFound(status) && !ignore404Redirect) {
    redirect404()

    return null
  }

  if (isApiResponseStatusUnauthorized(status) && !ignore401Logout) {
    keycloak.logout()
  }

  throw new ApiError(data, status)
}

function callApi<TResponse>(api: ApiType, domain?: string): Promise<TResponse> {
  const {
    endpoint,
    data,
    instance = KeycloakProtectedApi,
    method,
    options = {},
    params,
  } = api
  const {
    abortSignal,
    ignore401Logout,
    ignore404Redirect,
    withCredentials,
    onUploadProgress,
  } = options

  return instance
    .request({
      data: data
        ? Array.isArray(data)
          ? data
          : omitBy(data, isUndefined)
        : undefined,
      headers: getHeaders(),
      method,
      params: omitBy(params, isNil),
      url: getUrl(endpoint, domain),
      ...(abortSignal ? { signal: abortSignal } : null),
      ...(withCredentials ? { withCredentials: true } : null),
      ...(onUploadProgress ? { onUploadProgress } : null),
    })
    .then((response) => response.data)
    .catch((error) => handleError(error, ignore404Redirect, ignore401Logout))
}

export const deleteApi = <TResponse = unknown>(
  api: ApiDeleteType,
  domain?: string
) => callApi<TResponse>({ ...api, method: 'DELETE' }, domain)

export const getApi = <TResponse = unknown>(api: ApiGetType, domain?: string) =>
  callApi<TResponse>({ ...api, method: 'GET' }, domain)

export const postApi = <TResponse = unknown>(
  api: ApiPostType,
  domain?: string
) => callApi<TResponse>({ ...api, method: 'POST' }, domain)

export const patchApi = <TResponse = unknown>(
  api: ApiPatchType,
  domain?: string
) => callApi<TResponse>({ ...api, method: 'PATCH' }, domain)

export const putApi = <TResponse = unknown>(api: ApiPutType, domain?: string) =>
  callApi<TResponse>({ ...api, method: 'PUT' }, domain)
